import {
  ComponentPropsWithoutRef,
  forwardRef,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import cx from "classnames";
import styles from "./Page.module.scss";
import { PageSidebar } from "./PageSidebar";
import { PageSidebarHeader } from "./PageSidebarHeader";
import { PageHeader } from "./PageHeader";
import { PageContent } from "./PageContent";
import { PagePanel } from "./PagePanel";
import { PageFooter } from "./PageFooter";
import { PageContext } from "./PageContext";
import { useMergeRefs } from "../../hooks";

//--- Start Page Element ---//
/**
 * Props for the Page component
 * @extends ComponentPropsWithoutRef<"div">
 */
export type PageProps = ComponentPropsWithoutRef<"div">;

/**
 * Page component for creating full-page layouts with navigation and content areas.
 *
 * Features:
 * - Container for complete page layouts
 * - Supports sidebar navigation with collapsible state
 * - Header with breadcrumbs, chips, and action buttons
 * - Main content area with virtual scrolling
 * - Optional slide-out panels with responsive dialog fallback
 * - Footer with layout utilities
 * - Responsive design with mobile adaptations
 * - Accessibility support with proper landmarks
 * - Automatic tracking ID generation for analytics
 * - Local storage persistence for sidebar state
 *
 * @example
 * <Page>
 *   <Page.Sidebar currentPageTitle="Navigation">
 *     <Page.SidebarHeader>
 *       <Text variant="headline" el="h2">Navigation</Text>
 *     </Page.SidebarHeader>
 *     <SideNav>
 *       <SideNavLink pathname="/">Home</SideNavLink>
 *     </SideNav>
 *   </Page.Sidebar>
 *   <Page.Header
 *     title="Page Title"
 *     breadcrumbs={[{ href: "/", children: "Home" }]}
 *     actions={{ primary: { label: "Save", onClick: handleSave } }}
 *   />
 *   <Page.Content>
 *     <Text>Main content</Text>
 *   </Page.Content>
 *   <Page.Panel open={isPanelOpen} size="medium">
 *     <Text>Panel content</Text>
 *   </Page.Panel>
 *   <Page.Footer>
 *     <Text>Footer content</Text>
 *   </Page.Footer>
 * </Page>
 */
export const Page = Object.assign(
  forwardRef<HTMLDivElement, PageProps>(function PageInner(props, ref) {
    const { children, className, ...rest } = props;
    const classNames = cx(styles["page"], className);
    const elRef = useRef<HTMLDivElement>(null);
    const combinedRef = useMergeRefs([elRef, ref]);

    const [pageWidth, setPageWidth] = useState<number>(0);
    const [contentWrapper, setContentWrapper] = useState<HTMLElement | null>(
      null,
    );
    const [footerSlot, setFooterSlot] = useState<HTMLElement | null>(null);

    useLayoutEffect(() => {
      const element = elRef.current;
      const resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry) {
          setPageWidth(entry.contentRect.width);
        }
      });
      if (element) {
        resizeObserver.observe(element);
      }
      return () => {
        if (element) {
          resizeObserver.unobserve(element);
          resizeObserver.disconnect();
        }
      };
    }, [elRef]);

    const contextValue = useMemo(
      () => ({ pageWidth, contentWrapper, footerSlot }),
      [pageWidth, contentWrapper, footerSlot],
    );

    return (
      <PageContext.Provider value={contextValue}>
        <div className={classNames} data-anv="Page" ref={combinedRef} {...rest}>
          <div className={styles["container"]}>
            {children}
            <main
              className={styles["content-wrapper"]}
              ref={setContentWrapper}
            />
            <div className={styles["footer-slot"]} ref={setFooterSlot} />
          </div>
        </div>
      </PageContext.Provider>
    );
  }),
  {
    /**
     * PageSidebar component for creating collapsible navigation sidebars.
     *
     * Features:
     * - Collapsible sidebar with localStorage persistence
     * - Responsive design with adaptive popover on mobile
     * - Automatic tracking ID generation for analytics
     * - Keyboard navigation and accessibility support
     * - Toggle button with tooltip
     * - Context provider for sidebar state
     * - Mobile-friendly popover navigation
     *
     * @example
     * <Page.Sidebar currentPageTitle="Navigation">
     *   <Page.SidebarHeader>
     *     <Text variant="headline" el="h2">Sidebar Title</Text>
     *   </Page.SidebarHeader>
     *   <SideNav>
     *     <SideNavLink pathname="/">Home</SideNavLink>
     *   </SideNav>
     * </Page.Sidebar>
     */
    Sidebar: PageSidebar,
    /**
     * PageSidebarHeader component for creating header sections within sidebars.
     *
     * Features:
     * - Container for sidebar header content
     * - Automatic visibility handling based on sidebar collapsed state
     * - Supports all standard div props
     * - Context-aware styling
     *
     * @example
     * <Page.SidebarHeader>
     *   <Text variant="headline" el="h2" size="medium">
     *     Sidebar Title
     *   </Text>
     * </Page.SidebarHeader>
     */
    SidebarHeader: PageSidebarHeader,
    /**
     * PageHeader component for creating page headers with navigation and actions.
     *
     * Features:
     * - Page title with semantic heading
     * - Breadcrumb navigation support
     * - Status chips display
     * - Primary and secondary action buttons
     * - Preference action button/link
     * - Responsive layout with fluid grid
     * - Accessibility support with proper ARIA labels
     * - Flexible layout configuration
     * - Description content support
     *
     * @example
     * <Page.Header
     *   title="Page Title"
     *   breadcrumbs={[
     *     { href: "/", children: "Home" },
     *     { children: "Current Page" }
     *   ]}
     *   chips={[{ label: "Status" }]}
     *   actions={{
     *     primary: { label: "Save", onClick: handleSave },
     *     secondary: [{ label: "Cancel", onClick: handleCancel }]
     *   }}
     * />
     */
    Header: PageHeader,
    /**
     * PageContent component for the main content area of a page.
     *
     * Features:
     * - Semantic main element for page content
     * - Virtual scrolling support for long content
     * - Automatic scroll synchronization
     * - Responsive design
     * - Accessibility support with proper main landmark
     * - Scroll position management
     *
     * @example
     * <Page.Content>
     *   <Text>Main page content goes here</Text>
     *   <Button>Action Button</Button>
     * </Page.Content>
     */
    Content: PageContent,
    /**
     * PagePanel component for creating slide-out panels or dialogs.
     *
     * Features:
     * - Responsive design with dialog fallback on mobile
     * - Multiple size variants (small, medium, large)
     * - Optional padding control
     * - Persistent or controlled open state
     * - Automatic mobile dialog conversion
     * - Keyboard navigation and accessibility support
     * - Smooth animations and transitions
     *
     * @example
     * <Page.Panel open={isOpen} size="medium">
     *   <Text variant="headline" el="h3">Panel Content</Text>
     *   <Button onClick={() => setIsOpen(false)}>Close</Button>
     * </Page.Panel>
     */
    Panel: PagePanel,
    /**
     * PageFooter component for creating page footers with layout utilities.
     *
     * Features:
     * - Semantic footer element
     * - Layout utility props support
     * - Responsive design
     * - Accessibility support with proper footer landmark
     * - Flexible content positioning
     *
     * @example
     * <Page.Footer>
     *   <Text>Footer content with layout utilities</Text>
     * </Page.Footer>
     */
    Footer: PageFooter,
  },
);
Page.displayName = "Page";
